import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, Eye, EyeOff, Lock, Mail, Star } from 'lucide-react';
import {
  getExtendedSignupAnswers,
  saveExtendedSignupAnswer,
  setCurrentSignupFlow,
  trackSignupEvent,
  trackSignupEventOnce,
} from '../../lib/signupAnalytics';
import { saveSignupPlanSelection } from '../../lib/signupPlanSelection';
import { supabase } from '../../lib/supabase';
import { getPersistedReferralPartnerCode } from '../../lib/referralPartner';
import { sendSignupOtp, verifySignupOtp } from '../../lib/signupEmailVerification';
import { useSignup } from '../../hooks/useSignup';
import { isPasswordStrongEnough } from '../../lib/passwordStrength';
import PasswordRequirements from '../../components/auth/PasswordRequirements';

/**
 * Extended signup flow (/sign-up-new-business).
 *
 * Light-themed, one screen at a time:
 *   qualifying questions → email → 6-digit code → create password →
 *   business info → plan preference → account created → plan selection.
 *
 * Account/business creation reuses useSignup's submit (same records, same
 * redirect to plan activation as the standard flow). Qualifying answers are
 * stored per-session and attached to the signup_completed analytics event;
 * every event is tagged flow='extended' for funnel comparison.
 */

interface QualifyingOption {
  emoji: string;
  label: string;
  value: string;
}

interface QualifyingQuestion {
  id: string;
  title: string;
  options: QualifyingOption[];
  /** Conditional questions: shown only when this returns true for the answers so far. */
  showIf?: (answers: Record<string, string>) => boolean;
}

// Add more questions to this list as the flow grows — the progress bar,
// analytics steps, and answer storage all key off it automatically.
const QUALIFYING_QUESTIONS: QualifyingQuestion[] = [
  {
    id: 'business_age',
    title: 'How long have you been in business?',
    options: [
      { emoji: '🌱', label: 'Just starting out', value: 'just_starting' },
      { emoji: '🐣', label: 'Under 1 year', value: 'under_1_year' },
      { emoji: '📈', label: '1-3 years', value: '1_3_years' },
      { emoji: '🏆', label: '3-5 years', value: '3_5_years' },
      { emoji: '🏛️', label: '5+ years', value: '5_plus_years' },
    ],
  },
  {
    id: 'team_size',
    title: 'How big is your team?',
    options: [
      { emoji: '🙋', label: 'Just me', value: 'just_me' },
      { emoji: '👥', label: '2-5', value: '2_5' },
      { emoji: '👨‍👩‍👧‍👦', label: '5-10', value: '5_10' },
      { emoji: '🏢', label: '15-20', value: '15_20' },
      { emoji: '🚀', label: '20+', value: '20_plus' },
    ],
  },
  {
    id: 'goal',
    title: 'What is your goal?',
    options: [
      { emoji: '🚀', label: 'Get More Customers', value: 'get_more_customers' },
      { emoji: '✨', label: 'Look Professional Online', value: 'look_professional_online' },
      { emoji: '⚙️', label: 'Automate My Business', value: 'automate_my_business' },
      { emoji: '🕒', label: 'Track Employees', value: 'track_employees' },
      { emoji: '📊', label: 'Track Business Analytics', value: 'track_business_analytics' },
      { emoji: '❓', label: 'Other', value: 'other' },
    ],
  },
];

const PLAN_PREF_OPTIONS: QualifyingOption[] = [
  { emoji: '✨', label: 'Recommend a plan for me', value: 'recommend' },
  { emoji: '🧭', label: "I'll explore plans myself", value: 'explore' },
];

interface CatalogPlan {
  plan_id: string;
  plan_name: string;
  plan_price: number;
  trial_days: number | null;
  included_apps: string[];
}
interface CatalogAddon {
  id: string;
  name: string;
  description: string | null;
  app_id: string;
  price_monthly: number | null;
}
interface PlanRecommendation {
  plan_id: string;
  plan_name: string;
  plan_price: number;
  trial_days: number | null;
  addons: { id: string; name: string; description: string | null; price_monthly: number | null }[];
  headline: string;
  reasons: string[];
  total_monthly: number;
}

const prettyAppName = (appId: string) =>
  appId
    .replace('-core-app', '')
    .replace('-addon-app', '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const money = (n: number | null | undefined) => `$${(Number(n) || 0).toFixed(2)}`;

type Phase = 'questions' | 'email' | 'code' | 'password' | 'business' | 'plan' | 'recommendation' | 'plans' | 'setup-choice';

/** Segment 1 = quiz · segment 2 = email + code · segment 3 = password + business + plan. */
function ProgressSegments({ quizProgress, phase }: { quizProgress: number; phase: Phase }) {
  const seg1 = phase !== 'questions' ? 1 : quizProgress;
  const seg2 =
    phase === 'email' ? 0.4 : phase === 'code' ? 0.8 : ['password', 'business', 'plan', 'recommendation', 'plans', 'setup-choice'].includes(phase) ? 1 : 0;
  const seg3 =
    phase === 'password' ? 0.33 : phase === 'business' ? 0.6 : phase === 'plan' ? 0.75 : ['recommendation', 'plans'].includes(phase) ? 0.92 : phase === 'setup-choice' ? 0.98 : 0;
  const segments = [seg1, seg2, seg3];
  return (
    <div className="flex gap-1.5">
      {segments.map((fill, i) => (
        <div key={i} className="h-1 flex-1 rounded-full bg-gray-200 overflow-hidden">
          <div
            className="h-full rounded-full bg-gray-900 transition-all duration-300"
            style={{ width: `${Math.min(100, Math.max(0, fill * 100))}%` }}
          />
        </div>
      ))}
    </div>
  );
}

const inputClass =
  'w-full rounded-2xl border border-gray-200 bg-white px-4 py-4 text-[16px] text-gray-900 placeholder:text-gray-400 shadow-[0_1px_3px_rgba(0,0,0,0.05)] focus:outline-none focus:border-gray-400 transition-colors';

function ContinueButton({
  onClick,
  busy,
  busyLabel,
  children,
  disabled,
}: {
  onClick: () => void;
  busy?: boolean;
  busyLabel?: string;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || disabled}
      className="w-full flex items-center justify-center gap-2 rounded-full bg-gray-900 hover:bg-black px-6 h-[58px] text-[17px] font-semibold text-white transition-colors disabled:opacity-60"
    >
      {busy ? (
        <>
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
          {busyLabel || 'Working…'}
        </>
      ) : (
        <>
          {children}
          <ArrowRight className="h-5 w-5 text-emerald-300" />
        </>
      )}
    </button>
  );
}

export default function SignupNewBusiness() {
  const [questionIndex, setQuestionIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('questions');
  /** Answers so far — drives conditional questions (e.g. team size). */
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const referralCode = getPersistedReferralPartnerCode();

  const isVisible = (q: QualifyingQuestion, ans: Record<string, string>) => !q.showIf || q.showIf(ans);
  const nextVisibleIndex = (from: number, ans: Record<string, string>) => {
    for (let i = from; i < QUALIFYING_QUESTIONS.length; i++) {
      if (isVisible(QUALIFYING_QUESTIONS[i], ans)) return i;
    }
    return -1;
  };
  const prevVisibleIndex = (from: number, ans: Record<string, string>) => {
    for (let i = from; i >= 0; i--) {
      if (isVisible(QUALIFYING_QUESTIONS[i], ans)) return i;
    }
    return 0;
  };

  // Email + verification state (pre-account screens)
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [sendingCode, setSendingCode] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const codeInputRef = useRef<HTMLInputElement | null>(null);

  // Shared signup engine — owns the form fields, validation, and submit
  // (account + business creation and the redirect to plan activation).
  const {
    formData,
    setFormData,
    validationErrors,
    loading: creatingAccount,
    error: submitError,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    handleSubmit,
    prefillVerifiedEmail,
  } = useSignup();

  // Page-level validation messages for the password screen.
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [businessError, setBusinessError] = useState<string | null>(null);

  // Plan recommendation / exploration state.
  const [reco, setReco] = useState<PlanRecommendation | null>(null);
  const [recoLoading, setRecoLoading] = useState(false);
  const [catalog, setCatalog] = useState<{ plans: CatalogPlan[]; addons: CatalogAddon[] } | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [selectedAddonIds, setSelectedAddonIds] = useState<Set<string>>(new Set());
  const [planError, setPlanError] = useState<string | null>(null);
  // Plan locked in on the plan screens; the setup-choice step completes signup with it
  const [pendingPlan, setPendingPlan] = useState<{ planId: string; source: 'recommend' | 'explore' } | null>(null);

  // Tag this tab as the extended flow BEFORE any events fire, then log the top
  // of the funnel.
  useEffect(() => {
    setCurrentSignupFlow('extended');
    trackSignupEventOnce('ext:page_view:landing', {
      eventType: 'page_view',
      step: 'landing',
      flow: 'extended',
      referralCode,
    });
    trackSignupEventOnce(`ext:step_view:q_${QUALIFYING_QUESTIONS[0]?.id ?? 'email'}`, {
      eventType: 'step_view',
      step: QUALIFYING_QUESTIONS[0] ? `q_${QUALIFYING_QUESTIONS[0].id}` : 'email',
      flow: 'extended',
      referralCode,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const question = QUALIFYING_QUESTIONS[questionIndex];

  // Quiz progress over the questions actually shown for this user's answers.
  const visibleQuestions = QUALIFYING_QUESTIONS.filter((q) => isVisible(q, answers));
  const visibleIndex = question ? Math.max(0, visibleQuestions.findIndex((q) => q.id === question.id)) : 0;
  const quizProgress = visibleQuestions.length === 0 ? 1 : visibleIndex / visibleQuestions.length;

  const trackStepView = (step: string) =>
    trackSignupEventOnce(`ext:step_view:${step}`, { eventType: 'step_view', step, flow: 'extended', referralCode });
  const trackStepComplete = (step: string, data?: Record<string, unknown>) =>
    trackSignupEventOnce(`ext:step_complete:${step}`, {
      eventType: 'step_complete',
      step,
      flow: 'extended',
      data,
      referralCode,
    });

  const handleAnswer = (option: QualifyingOption) => {
    if (!question) return;
    const newAnswers = { ...answers, [question.id]: option.value };
    saveExtendedSignupAnswer(question.id, option.value);
    trackSignupEvent({
      eventType: 'step_complete',
      step: `q_${question.id}`,
      flow: 'extended',
      data: { question: question.id, answer: option.value },
      referralCode,
    });

    setAnswers(newAnswers);

    const nextIndex = nextVisibleIndex(questionIndex + 1, newAnswers);
    if (nextIndex >= 0) {
      setQuestionIndex(nextIndex);
      trackStepView(`q_${QUALIFYING_QUESTIONS[nextIndex].id}`);
    } else {
      setPhase('email');
      trackStepView('email');
    }
  };

  const handleBack = () => {
    switch (phase) {
      case 'setup-choice':
        setPhase(pendingPlan?.source === 'explore' ? 'plans' : 'recommendation');
        break;
      case 'recommendation':
      case 'plans':
        setPhase('plan');
        break;
      case 'plan':
        setPhase('business');
        break;
      case 'business':
        setPhase('password');
        break;
      case 'password':
        setPhase('email');
        break;
      case 'code':
        setPhase('email');
        setCode('');
        setCodeError(null);
        break;
      case 'email':
        setPhase('questions');
        setQuestionIndex(prevVisibleIndex(QUALIFYING_QUESTIONS.length - 1, answers));
        break;
      default:
        setQuestionIndex((i) => prevVisibleIndex(i - 1, answers));
    }
  };

  /** Email screen continue → availability check + OTP send, then the code screen. */
  const handleEmailContinue = async () => {
    const normalized = email.trim().toLowerCase();
    if (!/\S+@\S+\.\S+/.test(normalized)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setSendingCode(true);
    setEmailError(null);
    try {
      const res = await sendSignupOtp(normalized);
      if (res.available === false) {
        trackSignupEvent({
          eventType: 'error',
          step: 'email',
          flow: 'extended',
          data: { reason: res.reason === 'team_member' ? 'team_member_email' : 'email_in_use' },
          referralCode,
        });
        setEmailError(
          res.reason === 'team_member'
            ? 'This email belongs to a team member account. Log in instead, or use a different email.'
            : 'An account with this email already exists — log in instead.',
        );
        return;
      }
      if (!res.ok && res.error !== 'throttled') {
        setEmailError('Could not send a verification code. Please try again.');
        return;
      }
      setEmail(normalized);
      setCode('');
      setCodeError(null);
      setPhase('code');
      // Email rides along so the super-admin signup log can identify the visitor.
      trackStepComplete('email', { email: normalized });
      trackStepView('verify');
      setTimeout(() => codeInputRef.current?.focus(), 50);
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setCodeError('Enter the 6-digit code');
      return;
    }
    setVerifying(true);
    setCodeError(null);
    try {
      const res = await verifySignupOtp(email, trimmed);
      if (res.ok) {
        // Hand the verified email to the signup engine — submit won't re-ask.
        prefillVerifiedEmail(email);
        trackStepComplete('verify');
        trackStepView('password');
        setPhase('password');
        return;
      }
      const map: Record<string, string> = {
        expired: 'That code expired. Tap resend for a new one.',
        too_many: 'Too many attempts. Tap resend for a new code.',
        no_code: 'No code found — tap resend.',
        invalid: 'Incorrect code. Please try again.',
      };
      trackSignupEvent({
        eventType: 'error',
        step: 'verify',
        flow: 'extended',
        data: { reason: `otp_${res.error || 'invalid'}` },
        referralCode,
      });
      setCodeError(map[res.error || ''] || 'Incorrect code. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setCodeError(null);
    try {
      const res = await sendSignupOtp(email);
      if (res.error === 'throttled') {
        setCodeError(`Please wait ${res.retryAfter ?? 30}s before resending.`);
      } else if (!res.ok) {
        setCodeError('Could not resend. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  const handlePasswordContinue = () => {
    if (!formData.password || !isPasswordStrongEnough(formData.password)) {
      setPasswordError('Your password doesn\u2019t meet the requirements yet');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    setPasswordError(null);
    trackStepComplete('password');
    trackStepView('business');
    setPhase('business');
  };

  const handleBusinessContinue = () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setBusinessError('Please enter your first and last name');
      return;
    }
    if (!formData.businessName.trim()) {
      setBusinessError('Business name is required');
      return;
    }
    if (!formData.businessEmail.trim() || !/\S+@\S+\.\S+/.test(formData.businessEmail)) {
      setBusinessError('Please enter a valid business email');
      return;
    }
    if (!formData.businessPhone.trim()) {
      setBusinessError('Business phone is required');
      return;
    }
    setBusinessError(null);
    trackStepComplete('business_info');
    trackStepView('plan_preference');
    setPhase('plan');
  };

  /** Preference screen: branch into the AI recommendation or plan exploration. */
  const handlePlanPreference = (option: QualifyingOption) => {
    saveExtendedSignupAnswer('plan_preference', option.value);
    trackSignupEvent({
      eventType: 'step_complete',
      step: 'plan_preference',
      flow: 'extended',
      data: { answer: option.value },
      referralCode,
    });
    if (option.value === 'recommend') {
      setPhase('recommendation');
      trackStepView('plan_recommendation');
      void loadRecommendation();
    } else {
      setPhase('plans');
      trackStepView('plan_explore');
      void loadCatalog();
    }
  };

  /** Ask the AI to build the ultimate plan from the qualifying answers. */
  const loadRecommendation = async () => {
    if (!supabase) return;
    setRecoLoading(true);
    setPlanError(null);
    try {
      const { data, error } = await supabase.functions.invoke('ai-recommend-plan', {
        body: { answers: getExtendedSignupAnswers() || {} },
      });
      if (error || !data?.success || !data?.recommendation) throw new Error(data?.error || 'no_recommendation');
      setReco(data.recommendation as PlanRecommendation);
    } catch (err) {
      console.error('Plan recommendation failed:', err);
      // Don't dead-end the signup — fall through to exploring plans.
      setPhase('plans');
      trackStepView('plan_explore');
      void loadCatalog();
    } finally {
      setRecoLoading(false);
    }
  };

  /** Load the live plan + addon catalog (served by the edge function pre-auth). */
  const loadCatalog = async () => {
    if (!supabase || catalog) return;
    setCatalogLoading(true);
    setPlanError(null);
    try {
      const { data, error } = await supabase.functions.invoke('ai-recommend-plan', {
        body: { catalogOnly: true },
      });
      if (error || !data?.success) throw new Error(data?.error || 'catalog_failed');
      setCatalog({ plans: data.plans || [], addons: data.addons || [] });
    } catch (err) {
      console.error('Failed to load plan catalog:', err);
      setPlanError('Could not load plans. Please try again.');
    } finally {
      setCatalogLoading(false);
    }
  };

  /** Lock in the chosen plan (+ queued addons), then ask how they want to set up. */
  const finalizeWithPlan = async (planId: string, addonIds: string[], source: 'recommend' | 'explore') => {
    if (creatingAccount) return;
    saveSignupPlanSelection({ planId, addonIds, source });
    saveExtendedSignupAnswer('selected_plan', planId);
    trackSignupEvent({
      eventType: 'step_complete',
      step: 'plan_selected',
      flow: 'extended',
      data: { plan_id: planId, addon_ids: addonIds, source },
      referralCode,
    });
    setPendingPlan({ planId, source });
    setPhase('setup-choice');
    trackStepView('setup_choice');
  };

  /** Final step: guided vs self setup, then create the account. A guided
   * choice files a support request so the Nexus team sees who wants help. */
  const completeSignup = async (setupChoice: 'guided' | 'self') => {
    if (creatingAccount) return;
    saveExtendedSignupAnswer('setup_preference', setupChoice);
    trackSignupEvent({
      eventType: 'step_complete',
      step: 'setup_choice',
      flow: 'extended',
      data: { setup_preference: setupChoice, plan_id: pendingPlan?.planId || null },
      referralCode,
    });

    await handleSubmit();

    if (setupChoice === 'guided' && supabase) {
      // Best-effort after account creation: if signup failed there's no user
      // and this is skipped; never block the signup redirect on it.
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: biz } = await supabase
            .from('businesses')
            .select('id, name')
            .eq('owner_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          await supabase.from('support_requests').insert({
            user_id: user.id,
            business_id: biz?.id || null,
            subject: 'Guided setup requested (new signup)',
            description: `New business "${biz?.name || formData.businessName || 'Unknown'}" chose "Nexus guided setup" on the final signup step${pendingPlan?.planId ? ` (plan: ${pendingPlan.planId})` : ''}. Reach out to help them get set up.`,
          });
        }
      } catch (err) {
        console.warn('Failed to create guided-setup support request (non-critical):', err);
      }
    }
  };

  const selectedPlan = catalog?.plans.find((p) => p.plan_id === selectedPlanId) || null;
  // Addons offered for the selected plan: everything not already included —
  // higher-tier plans naturally show fewer addons.
  const offeredAddons =
    selectedPlan && catalog
      ? catalog.addons.filter((a) => !selectedPlan.included_apps.includes(a.app_id))
      : [];
  const exploreTotal =
    (selectedPlan?.plan_price || 0) +
    offeredAddons.filter((a) => selectedAddonIds.has(a.id)).reduce((sum, a) => sum + (Number(a.price_monthly) || 0), 0);

  const setField = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  // ── Layout chrome ──
  const header = (
    <div className="relative flex items-center justify-center py-5">
      {(questionIndex > 0 || phase !== 'questions') && (
        <button
          type="button"
          onClick={handleBack}
          className="absolute left-0 p-2 -ml-2 text-gray-500 hover:text-gray-800 transition-colors"
          aria-label="Back"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}
      <div className="text-xl font-extrabold tracking-tight">NexusAI</div>
    </div>
  );

  return (
    <div
      className="min-h-screen bg-white text-gray-900"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
      }}
    >
      <div className="max-w-xl mx-auto px-5 sm:px-6 flex flex-col min-h-screen">
        {header}
        <ProgressSegments quizProgress={quizProgress} phase={phase} />

        {/* ── Qualifying questions ── */}
        {phase === 'questions' && question && (
          <>
            <h1 className="mt-12 text-[32px] leading-[1.15] font-bold tracking-tight max-w-sm">
              {question.title}
            </h1>
            <div className="mt-8 space-y-3 pb-12">
              {question.options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleAnswer(option)}
                  className="w-full flex items-center gap-4 rounded-2xl border border-gray-200 bg-white px-4 py-4 text-left shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:border-gray-300 hover:shadow-md active:scale-[0.99] transition-all"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-50 border border-gray-100 text-[22px]">
                    {option.emoji}
                  </span>
                  <span className="text-[16px] font-semibold text-gray-900">{option.label}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* ── Email ── */}
        {phase === 'email' && (
          <div className="flex-1 flex flex-col">
            <div className="flex-1 flex flex-col justify-center pb-6">
              <h1 className="text-[32px] leading-[1.15] font-bold tracking-tight">Enter your email</h1>
              <p className="mt-3 text-[16px] text-gray-600">Save your results and get started.</p>

              <div className="mt-8">
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void handleEmailContinue();
                    }}
                    placeholder="Add your email"
                    className={`${inputClass} pl-12`}
                  />
                </div>

                {emailError && (
                  <p className="mt-2.5 text-[13px] font-medium text-red-600">
                    {emailError}
                    {emailError.includes('already exists') && (
                      <>
                        {' '}
                        <Link to="/login" className="underline font-semibold">Log in</Link>
                      </>
                    )}
                  </p>
                )}

                <div className="mt-4 flex items-center gap-2.5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <Star className="h-4 w-4 text-gray-900 fill-gray-900 shrink-0" />
                  <span className="text-[14px] font-medium text-gray-900">
                    5000+ Service businesses across North America run on Nexus.
                  </span>
                </div>
              </div>
            </div>

            <div className="pb-8">
              <ContinueButton onClick={() => void handleEmailContinue()} busy={sendingCode} busyLabel="Sending code…">
                Continue
              </ContinueButton>
              <p className="mt-5 text-center text-[15px] text-gray-500">
                Already have an account?{' '}
                <Link to="/login" className="font-bold text-gray-900 underline underline-offset-2">
                  Log in
                </Link>
              </p>
              <p className="mt-5 text-center text-[12px] leading-relaxed text-gray-400">
                By clicking Continue, you agree to our{' '}
                <a
                  href="https://www.nexusbookai.com/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 text-gray-500 hover:text-gray-700"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="https://www.nexusbookai.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 text-gray-500 hover:text-gray-700"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </div>
        )}

        {/* ── Verification code ── */}
        {phase === 'code' && (
          <div className="flex-1 flex flex-col">
            <div className="flex-1 flex flex-col justify-center pb-6">
              <h1 className="text-[32px] leading-[1.15] font-bold tracking-tight">Enter the code</h1>
              <p className="mt-3 text-[16px] text-gray-600">
                We sent a 6-digit code to <span className="font-semibold text-gray-900">{email}</span>.
              </p>

              <div className="mt-8">
                <input
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                    setCodeError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleVerifyCode();
                  }}
                  placeholder="••••••"
                  className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-4 text-center text-[26px] font-bold tracking-[0.5em] text-gray-900 placeholder:text-gray-300 shadow-[0_1px_3px_rgba(0,0,0,0.05)] focus:outline-none focus:border-gray-400 transition-colors"
                />

                {codeError && <p className="mt-2.5 text-[13px] font-medium text-red-600">{codeError}</p>}

                <p className="mt-4 text-center text-[14px] text-gray-500">
                  Didn't get it?{' '}
                  <button
                    type="button"
                    onClick={() => void handleResend()}
                    disabled={resending}
                    className="font-bold text-gray-900 underline underline-offset-2 disabled:opacity-50"
                  >
                    {resending ? 'Resending…' : 'Resend code'}
                  </button>
                </p>
              </div>
            </div>

            <div className="pb-8">
              <ContinueButton
                onClick={() => void handleVerifyCode()}
                busy={verifying}
                busyLabel="Verifying…"
                disabled={code.length !== 6}
              >
                Continue
              </ContinueButton>
            </div>
          </div>
        )}

        {/* ── Create password ── */}
        {phase === 'password' && (
          <div className="flex-1 flex flex-col">
            <div className="flex-1 flex flex-col justify-center pb-6">
              <h1 className="text-[32px] leading-[1.15] font-bold tracking-tight">Create a password</h1>
              <p className="mt-3 text-[16px] text-gray-600">Make it strong — this protects your whole business.</p>

              <div className="mt-8 space-y-3">
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={(e) => {
                      setField('password')(e);
                      setPasswordError(null);
                    }}
                    placeholder="Password"
                    className={`${inputClass} pl-12 pr-12`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>

                <PasswordRequirements password={formData.password} variant="light" />

                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={formData.confirmPassword}
                    onChange={(e) => {
                      setField('confirmPassword')(e);
                      setPasswordError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handlePasswordContinue();
                    }}
                    placeholder="Confirm password"
                    className={`${inputClass} pl-12 pr-12`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>

                {passwordError && <p className="text-[13px] font-medium text-red-600">{passwordError}</p>}
              </div>
            </div>

            <div className="pb-8">
              <ContinueButton onClick={handlePasswordContinue}>Continue</ContinueButton>
            </div>
          </div>
        )}

        {/* ── Business info (same fields as step 2 of the standard flow) ── */}
        {phase === 'business' && (
          <div className="flex-1 flex flex-col">
            <div className="flex-1 pb-6">
              <h1 className="mt-10 text-[32px] leading-[1.15] font-bold tracking-tight">
                Tell us about your business
              </h1>
              <p className="mt-3 text-[16px] text-gray-600">This sets up your account — you can change any of it later.</p>

              {/* Your info */}
              <p className="mt-8 mb-3 text-[13px] font-bold uppercase tracking-wider text-gray-400">
                Your info
              </p>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={(e) => {
                    setField('firstName')(e);
                    setBusinessError(null);
                  }}
                  placeholder="First name"
                  className={inputClass}
                />
                <input
                  type="text"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={(e) => {
                    setField('lastName')(e);
                    setBusinessError(null);
                  }}
                  placeholder="Last name"
                  className={inputClass}
                />
              </div>

              {/* Your business */}
              <p className="mt-7 mb-3 text-[13px] font-bold uppercase tracking-wider text-gray-400">
                Your business
              </p>
              <div className="space-y-3">
                <input
                  type="text"
                  autoComplete="organization"
                  value={formData.businessName}
                  onChange={(e) => {
                    setField('businessName')(e);
                    setBusinessError(null);
                  }}
                  placeholder="Business name"
                  className={inputClass}
                />
                <div>
                  <div className="relative">
                    <input
                      type="email"
                      value={formData.businessEmail}
                      readOnly
                      disabled
                      className={`${inputClass} pr-11 bg-gray-50 text-gray-500 cursor-not-allowed`}
                    />
                    <Lock className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  </div>
                  <p className="mt-1.5 ml-1 text-[12px] text-gray-400">
                    Locked to your verified email — you can add a separate customer-facing email later in settings.
                  </p>
                </div>
                <input
                  type="tel"
                  autoComplete="tel"
                  value={formData.businessPhone}
                  onChange={(e) => {
                    setField('businessPhone')(e);
                    setBusinessError(null);
                  }}
                  placeholder="Business phone"
                  className={inputClass}
                />
                <input
                  type="text"
                  autoComplete="street-address"
                  value={formData.businessAddress}
                  onChange={setField('businessAddress')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleBusinessContinue();
                  }}
                  placeholder="Business address (optional)"
                  className={inputClass}
                />

                {businessError && <p className="mt-1 text-[13px] font-medium text-red-600">{businessError}</p>}
              </div>
            </div>

            <div className="pb-8">
              <ContinueButton onClick={handleBusinessContinue}>Continue</ContinueButton>
            </div>
          </div>
        )}

        {/* ── Plan preference (final step → account creation → plan selection) ── */}
        {phase === 'plan' && (
          <>
            <h1 className="mt-12 text-[32px] leading-[1.15] font-bold tracking-tight max-w-md">
              Would you like Nexus to recommend a plan?
            </h1>
            <p className="mt-3 text-[16px] text-gray-600">
              Based on your goals, or browse every plan yourself — either way, you pick what's right.
            </p>

            <div className="mt-8 space-y-3 pb-12">
              {PLAN_PREF_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => void handlePlanPreference(option)}
                  disabled={creatingAccount}
                  className="w-full flex items-center gap-4 rounded-2xl border border-gray-200 bg-white px-4 py-4 text-left shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:border-gray-300 hover:shadow-md active:scale-[0.99] transition-all disabled:opacity-60"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-50 border border-gray-100 text-[22px]">
                    {option.emoji}
                  </span>
                  <span className="text-[16px] font-semibold text-gray-900">{option.label}</span>
                </button>
              ))}

              {creatingAccount && (
                <div className="flex items-center justify-center gap-2.5 pt-4 text-[14px] font-medium text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent" />
                  Creating your account…
                </div>
              )}
              {submitError && <p className="text-[13px] font-medium text-red-600">{submitError}</p>}
            </div>
          </>
        )}

        {/* ── AI plan recommendation ── */}
        {phase === 'recommendation' && (
          <div className="flex-1 flex flex-col pb-8">
            {recoLoading || !reco ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-[3px] border-gray-900 border-t-transparent" />
                <p className="text-[17px] font-semibold text-gray-900">Building your perfect setup…</p>
                <p className="text-[14px] text-gray-500 max-w-xs">
                  Analyzing your team size and goals against every plan and add-on.
                </p>
              </div>
            ) : (
              <>
                <h1 className="mt-10 text-[32px] leading-[1.15] font-bold tracking-tight">{reco.headline}</h1>
                <p className="mt-3 text-[16px] text-gray-600">
                  Built from your answers — here's the setup that fits how you work.
                </p>

                {/* Recommended plan card */}
                <div className="mt-8 rounded-2xl border-2 border-gray-900 bg-white p-5 shadow-[0_2px_10px_rgba(0,0,0,0.06)]">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="inline-block px-2 py-0.5 rounded-full bg-gray-900 text-white text-[10px] font-bold uppercase tracking-wide">
                        Recommended plan
                      </span>
                      <p className="mt-2 text-[20px] font-bold text-gray-900">{reco.plan_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[20px] font-bold text-gray-900">{money(reco.plan_price)}</p>
                      <p className="text-[12px] text-gray-400">/month</p>
                    </div>
                  </div>
                  {!!reco.trial_days && (
                    <p className="mt-2 text-[12px] font-semibold text-emerald-600">
                      Includes a {reco.trial_days}-day free trial
                    </p>
                  )}
                </div>

                {/* Recommended addons */}
                {reco.addons.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {reco.addons.map((addon) => (
                      <div key={addon.id} className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-50 border border-gray-100 text-[16px]">
                          ➕
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-[14px] font-semibold text-gray-900">{addon.name}</p>
                          {addon.description && (
                            <p className="text-[12px] text-gray-500 truncate">{addon.description}</p>
                          )}
                        </div>
                        <span className="text-[13px] font-semibold text-gray-700 whitespace-nowrap">
                          +{money(addon.price_monthly)}/mo
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Why this fits */}
                {reco.reasons.length > 0 && (
                  <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                    <p className="text-[13px] font-bold text-gray-900 mb-2">Why this fits you</p>
                    <ul className="space-y-1.5">
                      {reco.reasons.map((reason, i) => (
                        <li key={i} className="flex items-start gap-2 text-[13px] text-gray-700">
                          <span className="mt-0.5 text-emerald-600">✓</span>
                          {reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between px-1">
                  <span className="text-[14px] font-semibold text-gray-600">Total</span>
                  <span className="text-[18px] font-bold text-gray-900">{money(reco.total_monthly)}/mo</span>
                </div>

                {submitError && <p className="mt-2 text-[13px] font-medium text-red-600">{submitError}</p>}

                <div className="mt-5 space-y-3">
                  <ContinueButton
                    onClick={() =>
                      void finalizeWithPlan(reco.plan_id, reco.addons.map((a) => a.id), 'recommend')
                    }
                    busy={creatingAccount}
                    busyLabel="Creating your account…"
                  >
                    Continue with this setup
                  </ContinueButton>
                  <button
                    type="button"
                    onClick={() => {
                      setPhase('plans');
                      trackStepView('plan_explore');
                      void loadCatalog();
                    }}
                    disabled={creatingAccount}
                    className="w-full py-2 text-[15px] font-semibold text-gray-500 hover:text-gray-800 transition-colors disabled:opacity-50"
                  >
                    See all plans instead
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Explore plans (with addon picker) ── */}
        {phase === 'plans' && (
          <div className="flex-1 flex flex-col pb-8">
            {catalogLoading || !catalog ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-4">
                <div className="animate-spin rounded-full h-8 w-8 border-[3px] border-gray-900 border-t-transparent" />
                <p className="text-[15px] font-medium text-gray-500">Loading plans…</p>
                {planError && <p className="text-[13px] font-medium text-red-600">{planError}</p>}
              </div>
            ) : (
              <>
                <h1 className="mt-10 text-[32px] leading-[1.15] font-bold tracking-tight">Choose your plan</h1>
                <p className="mt-3 text-[16px] text-gray-600">
                  Pick a plan, then customize it with add-on apps.
                </p>

                {/* Plan cards */}
                <div className="mt-8 space-y-3">
                  {catalog.plans.map((plan) => {
                    const selected = selectedPlanId === plan.plan_id;
                    return (
                      <button
                        key={plan.plan_id}
                        type="button"
                        onClick={() => {
                          setSelectedPlanId(plan.plan_id);
                          // Drop queued addons the new plan already includes.
                          setSelectedAddonIds((prev) => {
                            const next = new Set(prev);
                            for (const id of prev) {
                              const addon = catalog.addons.find((a) => a.id === id);
                              if (!addon || plan.included_apps.includes(addon.app_id)) next.delete(id);
                            }
                            return next;
                          });
                        }}
                        className={`w-full rounded-2xl border-2 px-4 py-4 text-left transition-all ${
                          selected
                            ? 'border-gray-900 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.06)]'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-[17px] font-bold text-gray-900">{plan.plan_name}</p>
                          <div className="text-right">
                            <span className="text-[17px] font-bold text-gray-900">{money(plan.plan_price)}</span>
                            <span className="text-[12px] text-gray-400">/mo</span>
                          </div>
                        </div>
                        <p className="mt-1 text-[12px] text-gray-500">
                          {plan.included_apps.slice(0, 5).map(prettyAppName).join(' · ')}
                          {plan.included_apps.length > 5 ? ` · +${plan.included_apps.length - 5} more` : ''}
                        </p>
                        {!!plan.trial_days && (
                          <p className="mt-1.5 text-[12px] font-semibold text-emerald-600">
                            {plan.trial_days}-day free trial
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Addon picker for the selected plan */}
                {selectedPlan && (
                  <div className="mt-7">
                    <p className="text-[13px] font-bold uppercase tracking-wider text-gray-400 mb-1">
                      Make it yours — add-on apps
                    </p>
                    <p className="text-[13px] text-gray-500 mb-3">
                      {offeredAddons.length === 0
                        ? `${selectedPlan.plan_name} already includes every add-on.`
                        : 'Optional — tap to add. You can add or remove these anytime.'}
                    </p>
                    <div className="space-y-2">
                      {offeredAddons.map((addon) => {
                        const on = selectedAddonIds.has(addon.id);
                        return (
                          <button
                            key={addon.id}
                            type="button"
                            onClick={() =>
                              setSelectedAddonIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(addon.id)) next.delete(addon.id);
                                else next.add(addon.id);
                                return next;
                              })
                            }
                            className={`w-full flex items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition-all ${
                              on ? 'border-gray-900 bg-gray-50' : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                          >
                            <span
                              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                                on ? 'border-gray-900 bg-gray-900' : 'border-gray-300 bg-white'
                              }`}
                            >
                              {on && <span className="text-white text-[12px] font-bold">✓</span>}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-[14px] font-semibold text-gray-900">{addon.name}</p>
                              {addon.description && (
                                <p className="text-[12px] text-gray-500 truncate">{addon.description}</p>
                              )}
                            </div>
                            <span className="text-[13px] font-semibold text-gray-700 whitespace-nowrap">
                              +{money(addon.price_monthly)}/mo
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {planError && <p className="mt-3 text-[13px] font-medium text-red-600">{planError}</p>}
                {submitError && <p className="mt-3 text-[13px] font-medium text-red-600">{submitError}</p>}

                <div className="mt-6">
                  {selectedPlan && (
                    <div className="mb-3 flex items-center justify-between px-1">
                      <span className="text-[14px] font-semibold text-gray-600">Total</span>
                      <span className="text-[18px] font-bold text-gray-900">{money(exploreTotal)}/mo</span>
                    </div>
                  )}
                  <ContinueButton
                    onClick={() =>
                      selectedPlanId && void finalizeWithPlan(selectedPlanId, [...selectedAddonIds], 'explore')
                    }
                    busy={creatingAccount}
                    busyLabel="Creating your account…"
                    disabled={!selectedPlanId}
                  >
                    {selectedPlan ? `Continue with ${selectedPlan.plan_name}` : 'Select a plan to continue'}
                  </ContinueButton>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Final step: guided vs self setup ── */}
        {phase === 'setup-choice' && (
          <div className="flex-1 flex flex-col pb-8">
            <h1 className="mt-12 text-[32px] leading-[1.15] font-bold tracking-tight max-w-sm">
              How would you like to get set up?
            </h1>
            <p className="mt-3 text-[15px] text-gray-500 max-w-sm">
              Either way, your account is created now — this just decides how much help you get.
            </p>

            {submitError && <p className="mt-3 text-[13px] font-medium text-red-600">{submitError}</p>}

            <div className="mt-8 space-y-3">
              <button
                type="button"
                onClick={() => void completeSignup('guided')}
                disabled={creatingAccount}
                className="w-full flex items-center gap-4 rounded-2xl border-2 border-emerald-300 bg-emerald-50/60 px-4 py-5 text-left shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:border-emerald-400 hover:shadow-md active:scale-[0.99] transition-all disabled:opacity-60"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white border border-emerald-200 text-[22px]">
                  🤝
                </span>
                <span className="flex-1">
                  <span className="block text-[16px] font-bold text-gray-900">Get set up with Nexus help</span>
                  <span className="block mt-0.5 text-[13px] text-gray-600">
                    Our team reaches out and walks you through everything — recommended for new users
                  </span>
                </span>
              </button>

              <button
                type="button"
                onClick={() => void completeSignup('self')}
                disabled={creatingAccount}
                className="w-full flex items-center gap-4 rounded-2xl border border-gray-200 bg-white px-4 py-5 text-left shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:border-gray-300 hover:shadow-md active:scale-[0.99] transition-all disabled:opacity-60"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-50 border border-gray-100 text-[22px]">
                  🧭
                </span>
                <span className="flex-1">
                  <span className="block text-[16px] font-bold text-gray-900">I'll set up on my own</span>
                  <span className="block mt-0.5 text-[13px] text-gray-600">
                    Explore at your own pace — the AI Setup Wizard and help centre are always there
                  </span>
                </span>
              </button>
            </div>

            {creatingAccount && (
              <div className="mt-6 flex items-center justify-center gap-2 text-[14px] font-medium text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-900 border-t-transparent" />
                Creating your account…
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
